import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Box, CircularProgress } from '@mui/material';
import dayjs from 'dayjs';
import SnackbarAlert from '../components/SnackbarAlert';
import BookingList from '../components/BookingList';
import CalendarView from '../components/CalendarView';
import { fetchNextAvailableDates } from '../api/bookings';

function useManageBookings() {
  const [availableDates, setAvailableDates] = useState({ next_day: null, second_day: null });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [selectedDate, setSelectedDate] = useState(null);

  const loadAvailableDates = useCallback(async () => {
    try {
      const data = await fetchNextAvailableDates();
      setAvailableDates(data);
      if (data.next_day) {
        setSelectedDate(data.next_day);
      }
    } catch (err) {
      console.error('Error fetching available dates:', err);
    }
  }, []);

  const handleDayClick = useCallback(({ date }) => {
    const formattedDate = dayjs(date).format('YYYY-MM-DD');
    setSelectedDate(formattedDate);
  }, []);

  useEffect(() => {
    loadAvailableDates();
  }, [loadAvailableDates]);

  return {
    availableDates,
    selectedDate,
    snackbar,
    setSnackbar,
    handleDayClick,
  };
}

export default function ManageBookings() {
  const {
    availableDates,
    selectedDate,
    snackbar,
    setSnackbar,
    handleDayClick,
  } = useManageBookings();

  const calendarRef = useRef(null);

  const refreshCalendarData = () => {
    if (calendarRef.current) {
      calendarRef.current.refreshCalendarData();
    }
  };

  if (!availableDates.next_day) {
    return (
      <Box sx={{ p: 2 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', gap: 2 }}>
        <Box sx={{ flex: 2 }}>
          <CalendarView ref={calendarRef} onDayClick={handleDayClick} setSnackbar={setSnackbar} />
        </Box>
        <BookingList
          selectedDate={selectedDate}
          setSnackbar={setSnackbar}
          reloadCalendarData={refreshCalendarData}
        />
      </Box>

      <SnackbarAlert
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      />
    </Box>
  );
}
